import React from 'react';
import { Check } from 'lucide-react';
import { useInnerCircle } from '../context/InnerCircleContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import OrnateDivider from '../components/OrnateDivider';
import EditableText from '../components/EditableText';
import SEO from '../components/SEO';

const QUERIES_WA =
  'https://wa.me/917310777189?text=' +
  encodeURIComponent('Namaste Sanjay Ji, I have a question about the Inner Circle membership.');

const InnerCirclePage = () => {
  const { openIC } = useInnerCircle();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Members land on their manage page instead of this marketing page.
  React.useEffect(() => {
    if (user?.is_member) navigate('/account/membership', { replace: true });
  }, [user?.is_member, navigate]);

  return (
    <div className="min-h-screen pt-28 pb-20 px-4" data-testid="inner-circle-page">
      <SEO
        title="The Inner Circle · Siddha Samaj"
        description="Deeper teachings from the Siddha tradition, shared with those who wish to go further."
      />
      <div className="max-w-2xl mx-auto">
        <OrnateDivider variant="lotus" className="mb-6 max-w-md mx-auto" />

        <div className="text-center">
          <div className="text-[11px] font-heading tracking-[0.3em] text-primary/55 uppercase mb-3">
            <EditableText id="ic.page.eyebrow" defaultValue="Membership" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold golden-text text-glow tracking-wide mb-4">
            <EditableText id="ic.page.heading" defaultValue="The Inner Circle" />
          </h1>
          <p className="text-sm sm:text-base text-primary/80 italic max-w-xl mx-auto leading-relaxed mb-10">
            <EditableText
              id="ic.page.intro"
              defaultValue="Deeper teachings from the Siddha tradition, shared with those who wish to go further."
              multiline
            />
          </p>
        </div>

        <div className="card-mystical rounded-2xl p-6 sm:p-8 mb-10" data-testid="ic-page-benefits">
          <div className="text-[11px] font-heading tracking-[0.3em] text-primary/55 uppercase text-center mb-5">
            <EditableText id="ic.page.benefits.eyebrow" defaultValue="What you receive" />
          </div>
          <ul className="space-y-4">
            {[
              { id: 1, text: 'Two exclusive Esoteric Inner Circle shares every month, not published on the open blog' },
              { id: 2, text: 'Access to Dhyana Lab and esoteric Siddha meditation experiments (opening soon)' },
              { id: 3, text: '10% off ebooks' },
              { id: 4, text: 'Special emailers and inner circle community access' },
            ].map((b) => (
              <li key={b.id} className="flex items-start gap-3">
                <Check size={16} className="text-primary shrink-0 mt-1" />
                <span className="text-sm sm:text-[15px] text-primary/85 leading-relaxed">
                  <EditableText id={`ic.page.bullet.${b.id}`} defaultValue={b.text} multiline />
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Plans summary */}
        <div className="grid grid-cols-2 gap-4 mb-6" data-testid="ic-page-plans">
          <div className="card-mystical rounded-2xl p-5 text-center">
            <div className="text-[10px] font-heading tracking-[0.3em] text-primary/60 uppercase">Monthly</div>
            <div className="mt-2 text-3xl font-heading golden-text">₹108</div>
            <div className="text-[11px] text-primary/60 mt-1">per month</div>
          </div>
          <div className="card-mystical rounded-2xl p-5 text-center border-primary">
            <div className="text-[10px] font-heading tracking-[0.3em] text-primary/60 uppercase">Yearly</div>
            <div className="mt-2 text-3xl font-heading golden-text">₹1,100</div>
            <div className="text-[11px] text-primary/60 mt-1">saves ₹196</div>
          </div>
        </div>

        <p className="text-xs text-primary/60 italic text-center mb-2 leading-relaxed">
          <EditableText
            id="ic.page.renewalNote"
            defaultValue="Renews automatically. Cancel anytime from your account. Access continues until the end of the period you have paid for."
            multiline
          />
        </p>
        <p className="text-xs text-primary/80 text-center tracking-[0.3em] uppercase mb-6">
          <EditableText id="ic.page.cancelAnytime" defaultValue="Cancel anytime" />
        </p>

        <div className="text-center">
          <button
            type="button"
            onClick={() => openIC()}
            className="ornate-button px-10 py-3 text-sm font-heading uppercase tracking-widest text-primary rounded-lg"
            data-testid="ic-page-join"
          >
            <EditableText id="ic.page.cta" defaultValue="Join the Inner Circle" />
          </button>
        </div>

        <div className="mt-10 text-center">
          <a
            href={QUERIES_WA}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ic-page-queries-whatsapp"
            className="inline-flex items-center gap-2 text-[11px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/40 text-primary/85 hover:text-primary hover:border-primary/70 px-4 py-2"
          >
            <EditableText id="ic.page.queries" defaultValue="For any queries · WhatsApp" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default InnerCirclePage;
